import React, { useEffect, useState } from "react";
import {
  Card,
  Button,
  Icon,
  CardActions,
  CardContent,
  Grid2,
  Typography,
  Stack,
  Box,
  CardMedia,
} from "@mui/material";
import classNames from "classnames";
import { currencyFormat } from "helpers/currency-utils";
import {
  addItem,
  removeItem,
  UpdateRentalItemsResponse,
} from "services/rentals-thin.service";
import {
  GlobalState,
  Rental,
  store,
  updateTotals,
  Car,
} from "store/rentals-store";
import { useSelector } from "react-redux";

export interface CarCardProps {
  car: Car;
}

export default function VehicleCard({ car }: CarCardProps) {
  const [isAdded, setIsAdded] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const rental: Rental = useSelector((state: GlobalState) => state.rental);

  const updateCar = async (carId: string) => {
    let res: UpdateRentalItemsResponse;
    setIsUpdating(true);

    if (isAdded) {
      res = await removeItem(carId);
    } else {
      res = await addItem(carId, rental.startDate, rental.endDate);
    }
    store.dispatch(updateTotals(res.data));
    setIsUpdating(false);
  };

  const {
    brand,
    model,
    manufacturingYear,
    pricePerDay,
    passengers,
    transmission,
    bags,
  } = car;

  useEffect(() => {
    if (rental.totals) {
      const isItemInCart: boolean = !!rental.totals.items.find(
        (item) => item.id === car.id,
      );
      setIsAdded(isItemInCart);
    }
  }, [rental.totals, setIsAdded, car.id]);

  return (
    <Card
      className="vehicle-card"
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        width: "100%",
      }}
    >
      <Box
        sx={{
          width: { xs: "100%", md: 300 },
          height: { xs: 180, sm: 200, md: 250 },
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        <CardMedia
          component="img"
          image={car.images[0]}
          alt={`${brand}-${model}-${manufacturingYear}`}
          sx={{
            maxWidth: "100%",
            maxHeight: "100%",
            width: "auto",
            height: "auto",
            objectFit: "contain",
          }}
        />
      </Box>
      <CardContent
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          flex: 1,
          width: { xs: "100%", md: "auto" },
        }}
      >
        <Stack spacing={2} sx={{ minWidth: { xs: 0, md: 200 }, width: "100%" }}>
          <Box data-testid="vehicle-title">
            {`${brand} ${model} ${manufacturingYear}`}
            <Box>
              <small>Propietario: {car.agency.name}</small>
            </Box>
          </Box>
          <Grid2 container>
            <Grid2 size={{ xs: 5 }} data-testid="passengers">
              <Icon>people</Icon>
              {passengers}
            </Grid2>
            <Grid2 size={{ xs: 7 }} data-testid="transmission">
              <Icon>nature</Icon>
              {transmission}
            </Grid2>
          </Grid2>
          <Grid2 container>
            <Grid2 size={{ xs: 5 }} data-testid="bags">
              <Icon>card_travel</Icon>
              {bags}
            </Grid2>
            <Grid2 size={{ xs: 7 }}>
              <Typography fontWeight={"bold"} data-testid="pricePerDay">
                {currencyFormat(pricePerDay)}/día
              </Typography>
            </Grid2>
          </Grid2>
        </Stack>
        <CardActions
          sx={{
            p: 0,
            pt: 2,
            justifyContent: { xs: "center", md: "center" },
          }}
        >
          <Button
            key={`btn-${car.id}`}
            className={classNames("add-button", { remove: isAdded })}
            disabled={isUpdating}
            onClick={() => updateCar(car.id)}
            startIcon={<Icon>{isAdded ? "cancel" : "add"}</Icon>}
          >
            {isAdded ? "Remover" : "Agregar"}
          </Button>
        </CardActions>
      </CardContent>
    </Card>
  );
}
